import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import { string, oneOf, func, bool } from 'prop-types';
import { MOBILE } from '@thd-olt-functional/utils';
import { Drawer, DrawerItem, DrawerHeader } from '@thd-olt-component-react/drawer';
import { DualPathTooltip } from './DualPathTooltip';
import { ProductAddOnsPod } from '../../common/product-add-ons-pod';
import { useTippyToggle } from '../../../hooks/useTippyToggle';
import { dualPathContactInformation } from '../../../utils/product-add-on-constants';
import styles from './dual-path.scss';

const DualPath = ({
  channel,
  itemId,
  onChange,
  category,
  serviceUrl,
  value
}) => {
  const [checked, setChecked] = useState(value);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const tooltipButtonRef = useTippyToggle(checked);
  const techshedUrl = `${serviceUrl}/?${itemId}&pageType=PIP#`;
  const isMobile = channel === MOBILE;
  const drawerMaxSize = isMobile ? 100 : 500;

  const DUAL_PATH_FORM_TITLE = {
    brio_inaisle_sfi_twht: 'Tankless Water Heater Installation',
    brio_inaisle_sfi_whtr: 'Water Heater Installation',
    BRIO_INAISLE_SFI_FENC: 'Fence Installation',
    dualpath_inaisle_sfi_generators: 'Generator Installation',
    dualpath_inaisle_sfi_windows: 'Window Installation',
    'dreamline-door-installation/44fbb1b75': 'Dreamline Shower Door Installation',
    'plumbing-repair-dp/9f2b4389b': 'Plumbing Repair',
    'water-heater-repair-dp/201c992c0': 'Water Heater Repair'
  };

  const headerDescription = DUAL_PATH_FORM_TITLE[category];

  let cx = classNames.bind(styles);

  const actionButtonClasses = cx(
    'bttn bttn--atc buybox__add-on__request-a-quote',
    { 'u--hide': !checked }
  );

  const actionButtonWrapperClasses = cx(
    'dual-path__action-button',
    { 'u--hide': !checked }
  );

  const handleChange = (evt) => {
    LIFE_CYCLE_EVENT_BUS.trigger('dual-path.change', { isOptedIn: !checked, itemId });
    onChange({
      itemId,
      checked: evt.target.checked,
      addOnType: evt.target.value
    });
    setChecked(!checked);
  };

  const triggerAnalyticsEvent = () => {
    window.LIFE_CYCLE_EVENT_BUS.trigger('dualPath.request-a-quote-action', { programCategory: category });
  };

  const requestAQuote = () => {
    setIsDrawerOpen(true);
    triggerAnalyticsEvent();
  };

  const onDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  // eslint-disable-next-line react/prop-types
  const ContactInformation = ({ dualPathType = null }) => {
    if (!dualPathContactInformation[dualPathType]) {
      return null;
    }
    const tel = dualPathContactInformation[dualPathType]?.tel;
    return (
      <div className="dual-path__install-text">Or call
        <a className="u__default-link" href={`tel:${tel}`}>{dualPathContactInformation[dualPathType].label}</a>
      </div>
    );
  };

  const subHeading = useMemo(() => {
    if (!checked && ['brio_inaisle_sfi_twht', 'brio_inaisle_sfi_whtr'].includes(category)) {
      return (
        <div>Need help fast? Call&nbsp;
          <a className="u__default-link" href={`tel:${dualPathContactInformation[category].tel}`}>
            {dualPathContactInformation[category].label}
          </a>
        </div>
      );
    }

    return <div>A local pro will take care of the job for you.</div>;
  }, [checked, category]);

  return (
    <>
      <ProductAddOnsPod
        channel={channel}
        heading="Get Expert In-Home Installation"
        subHeading={subHeading}
        icon="use-and-care-manual.svg"
        iconHeight={36}
        iconWidth={36}
        onChange={handleChange}
        tooltip={(
          <DualPathTooltip
            channel={channel}
            category={category}
            ref={tooltipButtonRef}
          />
        )}
        userOptions={[
          {
            id: 'item ID',
            value: 'dual-path',
            checked,
            label: 'Add In-Home Installation'
          }
        ]}
      >
        <div className={actionButtonWrapperClasses}>
          <button type="button" className={actionButtonClasses} onClick={requestAQuote}>
            <span className="bttn__content">Request a Quote</span>
          </button>
          <ContactInformation dualPathType={category} />
        </div>
      </ProductAddOnsPod>
      {isDrawerOpen && (
        <Drawer
          isMobile={isMobile}
          maxSize={drawerMaxSize}
          open={isDrawerOpen}
          onClose={onDrawerClose}
          initialItem="product-quote"
        >
          <DrawerItem name="product-quote">
            <DrawerHeader title={headerDescription} />
            <iframe title="Request A Quote" src={techshedUrl} width="100%" height="99%" />
          </DrawerItem>
        </Drawer>
      )}
    </>
  );
};

DualPath.displayName = 'ProductAddOns-DualPath';

DualPath.propTypes = {
  /** What view to render ['desktop' || 'mobile']  */
  channel: oneOf(['desktop', 'mobile']),
  itemId: string.isRequired,
  onChange: func.isRequired,
  serviceUrl: string.isRequired,
  category: string,
  value: bool
};

DualPath.defaultProps = {
  channel: 'desktop',
  category: 'brio_inaisle_sfi_generalfs',
  value: false
};

export { DualPath };